import shopApi from '@/api/client';
import { getProductImageFolder } from '@/utils/productImage';

const POSITION_FIELDS = ['position_bezeichnung', 'artikel_json', 'anzahl', 'notizen'];
const CATALOG_ROUTES = {
  articles: 'artikel', steps: 'schritte', stepRules: 'schrittregeln',
  stepPrices: 'schrittpreise', surcharges: 'aufpreise', ralColors: 'ral_farben',
  priceLists: 'preislisten', fittingProperties: 'beschlageigenschaften',
};

export function createInternalConfiguratorApi(getProject) {
  return {
    async loadCatalog() {
      const catalog = {};
      for (const [key, route] of Object.entries(CATALOG_ROUTES)) {
        catalog[key] = (await shopApi.get('/api/' + route)).data;
      }
      return catalog;
    },
    lookupRasterPrice: (values, options) => shopApi.post('/api/preislistenfelder/searchlowestprice', values, options),
    loadProductImages: ({ variant, folder, priority }, options = {}) => shopApi.get(
      '/api/images/' + getProductImageFolder(folder || String(variant || '').split(' - ')[0]),
      { ...options, params: { priority } },
    ),
    imageUrl: image => 'https://p684534.mittwaldserver.info' + image.url,
    async loadProject() {
      const project = getProject();
      const response = await shopApi.get('/api/gespeicherteartikel/projekt/' + project.id, { params: {} });
      return { ...project, positions: response.data };
    },
    createPosition: (values, options) => options
      ? shopApi.post('/api/gespeicherteartikel', values, options)
      : shopApi.post('/api/gespeicherteartikel', values),
    updatePosition: (id, values) => shopApi.put('/api/gespeicherteartikel/' + id, values),
    deletePosition: id => shopApi.delete('/api/gespeicherteartikel/' + id, { data: {} }),
  };
}

export function createExternalConfiguratorApi(client, context) {
  let queue = Promise.resolve();
  let generation = 0;
  const loadProject = async () => {
    const { data } = await client.get('/project');
    if (data.id !== context.project.id) throw new Error('Das Integrationsprojekt stimmt nicht mit der Session überein.');
    context.project = data;
    return data;
  };
  const write = (operation) => {
    context.pendingWrites += 1;
    const scheduledGeneration = generation;
    const task = queue.then(async () => {
      if (scheduledGeneration !== generation) {
        throw new Error('Der Projektstand wurde neu geladen. Bitte die Änderung erneut prüfen.');
      }
      try {
        const response = await operation(context.project.integration_revision);
        context.project.integration_revision = response.data.revision;
        return response.data.position ? { ...response, data: response.data.position } : response;
      } catch (error) {
        generation += 1;
        // Reload persisted state; never repeat an uncertain or conflicting write.
        try { await loadProject(); } catch { /* Keep the original request error. */ }
        throw error;
      }
    });
    queue = task.catch(() => {});
    return task.finally(() => { context.pendingWrites -= 1; });
  };
  const positionValues = values => Object.fromEntries(
    POSITION_FIELDS.filter(key => Object.prototype.hasOwnProperty.call(values, key)).map(key => [key, values[key]])
  );
  return {
    async loadCatalog() {
      if (!context.catalog) context.catalog = (await client.get('/catalog')).data;
      return context.catalog;
    },
    lookupRasterPrice: (values, options) => client.post('/catalog/price-lookup', values, options),
    loadProductImages: ({ articleId, variant, priority }, options = {}) => client.get(
      '/catalog/articles/' + articleId + '/images', { ...options, params: { variant, priority } },
    ),
    imageUrl: image => client.resolveAssetUrl(image.url),
    loadProject,
    createPosition: values => write(revision => client.post('/project/positions', { ...positionValues(values), revision })),
    updatePosition: (id, values) => write(revision => client.put('/project/positions/' + id, { ...positionValues(values), revision })),
    deletePosition: id => write(revision => client.delete('/project/positions/' + id, { data: { revision } })),
  };
}
